import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "./api";

const AuthContext = createContext(null);

// Ein sessionStorage-Marker, der das ,,Werbung schon gezeigt"-Flag im
// aktuellen Login-Zyklus hält. Beim Login/Logout ausdrücklich zurücksetzen,
// damit jeder ECHTE neue Login-Vorgang die Anzeige wieder freigibt. Reloads
// und Navigation innerhalb der Session bleiben unbeeindruckt (sessionStorage
// überlebt den Reload, wird aber beim Tab-Close verworfen).
const AD_SESSION_KEY = "loop.ad_shown_session";
function resetAdCycle() {
  try { sessionStorage.removeItem(AD_SESSION_KEY); } catch { /* SSR / private-mode */ }
}

function hasCsrfCookie() {
  return document.cookie.split(";").some((c) => c.trim().startsWith("csrf_token="));
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, obj={principal_type,...}
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    if (!hasCsrfCookie()) { setUser(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch { setUser(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Neuer Login-Zyklus → Werbe-Anzeige wieder freischalten.
      resetAdCycle();
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      return { ok: false };
    }
  };

  const logout = async () => {
    // Route to the correct logout endpoint based on principal type.
    try {
      if (user && user.principal_type === "customer") {
        await api.post("/customer/logout");
      } else {
        await api.post("/auth/logout");
      }
    } catch {}
    // Login-Zyklus explizit beenden — bei Neuanmeldung soll Werbung wieder
    // erscheinen, unabhängig davon, ob derselbe Tab weiterläuft.
    resetAdCycle();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, error, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
